import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import { AssigneeSelect } from './AssigneeSelect';
import { 
  PlayCircle, Plus, ChevronDown, ChevronRight, MessageSquare, 
  Sparkles, Bot, User, Clock, ListChecks, Loader2, Send, Trash2,
  FileEdit, CheckCircle2, Circle, Save, BookOpen, ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';
import { playbookNotesAPI, salesPlaybookAPI } from '../api';
import { format, formatDistanceToNow } from 'date-fns';

export const PlaybookNotesSection = ({ entityType, entityId, className = "" }) => {
  const [playbooks, setPlaybooks] = useState([]);
  const [selectedPlaybook, setSelectedPlaybook] = useState(null);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [expandedNotes, setExpandedNotes] = useState({});
  const [generatingAI, setGeneratingAI] = useState(false);
  
  // View/Edit note dialog
  const [showNoteDetail, setShowNoteDetail] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [editingNote, setEditingNote] = useState(false);
  const [editedResponse, setEditedResponse] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  
  // Editable playbook state
  const [showPlaybookEditor, setShowPlaybookEditor] = useState(false);
  const [editingPlaybook, setEditingPlaybook] = useState(null);
  const [playbookResponses, setPlaybookResponses] = useState({});
  const [savingPlaybook, setSavingPlaybook] = useState(false);
  const [generatingStepAI, setGeneratingStepAI] = useState({});
  
  const [newNote, setNewNote] = useState({
    step_index: 0,
    question_text: '',
    response: '',
    ai_suggestion: '',
    create_task: false,
    task_title: '',
    task_description: '',
    task_due_date: '',
    task_assigned_to: ''
  });

  // Open note detail dialog
  const openNoteDetail = (note) => {
    setSelectedNote(note);
    setEditedResponse(note.response || '');
    setEditingNote(false);
    setShowNoteDetail(true);
  };
  
  // Save edited note
  const handleSaveNoteEdit = async () => {
    if (!selectedNote || !editedResponse.trim()) return;
    
    try {
      setSavingNote(true);
      await playbookNotesAPI.update(selectedNote.id, {
        response: editedResponse
      });
      toast.success('Note updated');
      setEditingNote(false);
      fetchNotes();
      // Update the selectedNote with new response
      setSelectedNote(prev => ({ ...prev, response: editedResponse }));
    } catch (error) {
      console.error('Failed to update note:', error);
      toast.error('Failed to update note');
    } finally {
      setSavingNote(false);
    }
  };

  useEffect(() => {
    fetchPlaybooks();
    fetchNotes();
  }, [entityType, entityId]);

  const fetchPlaybooks = async () => {
    try {
      const res = await salesPlaybookAPI.getAll();
      setPlaybooks(res.data || []);
    } catch (error) {
      console.error('Failed to fetch playbooks:', error);
    }
  };

  const fetchNotes = async () => {
    try {
      setLoading(true);
      const res = await playbookNotesAPI.getAll(entityType, entityId);
      setNotes(res.data.notes || []);
    } catch (error) {
      console.error('Failed to fetch playbook notes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!selectedPlaybook || !newNote.response) {
      toast.error('Please select a playbook and provide a response');
      return;
    }

    try {
      const noteData = {
        playbook_id: selectedPlaybook.id,
        entity_type: entityType,
        entity_id: entityId,
        step_index: newNote.step_index,
        question_text: newNote.question_text || selectedPlaybook.steps?.[newNote.step_index]?.title || '',
        response: newNote.response,
        ai_suggestion: newNote.ai_suggestion,
        followup_task: newNote.create_task ? {
          title: newNote.task_title || `Followup: ${selectedPlaybook.name}`,
          description: newNote.task_description,
          due_date: newNote.task_due_date,
          assigned_to: newNote.task_assigned_to,
          priority: 'medium'
        } : null
      };

      await playbookNotesAPI.create(noteData);
      toast.success('Playbook note saved');
      setShowAddDialog(false);
      setNewNote({
        step_index: 0,
        question_text: '',
        response: '',
        ai_suggestion: '',
        create_task: false,
        task_title: '',
        task_description: '',
        task_due_date: '',
        task_assigned_to: ''
      });
      setSelectedPlaybook(null);
      fetchNotes();
    } catch (error) {
      toast.error('Failed to save note');
    }
  };

  const handleGenerateAISuggestion = async () => {
    if (!selectedPlaybook || !newNote.response) {
      toast.error('Please provide a response first');
      return;
    }

    try {
      setGeneratingAI(true);
      const res = await playbookNotesAPI.generateAISuggestion({
        entity_type: entityType,
        entity_id: entityId,
        playbook_id: selectedPlaybook.id,
        question_text: newNote.question_text || selectedPlaybook.steps?.[newNote.step_index]?.title || '',
        user_response: newNote.response
      });

      if (res.data.suggestion) {
        setNewNote({ ...newNote, ai_suggestion: res.data.suggestion });
        toast.success('AI suggestion generated');
      } else {
        toast.error(res.data.error || 'Failed to generate suggestion');
      }
    } catch (error) {
      toast.error('Failed to generate AI suggestion');
    } finally {
      setGeneratingAI(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Delete this playbook note?')) return;
    
    try {
      await playbookNotesAPI.delete(noteId);
      toast.success('Note deleted');
      fetchNotes();
    } catch (error) {
      toast.error('Failed to delete note');
    }
  };

  const toggleNoteExpand = (noteId) => {
    setExpandedNotes(prev => ({ ...prev, [noteId]: !prev[noteId] }));
  };

  // Open playbook as editable document
  const openPlaybookEditor = (playbook) => {
    setEditingPlaybook(playbook);
    // Initialize responses from existing notes
    const existingResponses = {};
    const playbookNotes = notes.filter(n => n.playbook_id === playbook.id);
    playbookNotes.forEach(note => {
      existingResponses[note.step_index] = {
        response: note.response,
        ai_suggestion: note.ai_suggestion || '',
        note_id: note.id
      };
    });
    setPlaybookResponses(existingResponses);
    setShowPlaybookEditor(true);
  };

  // Generate AI insight for a specific step
  const handleGenerateStepAI = async (stepIndex, stepTitle, response) => {
    if (!response) {
      toast.error('Please provide a response first');
      return;
    }

    try {
      setGeneratingStepAI(prev => ({ ...prev, [stepIndex]: true }));
      const res = await playbookNotesAPI.generateAISuggestion({
        entity_type: entityType,
        entity_id: entityId,
        playbook_id: editingPlaybook.id,
        question_text: stepTitle,
        user_response: response
      });

      if (res.data.suggestion) {
        setPlaybookResponses(prev => ({
          ...prev,
          [stepIndex]: { ...prev[stepIndex], ai_suggestion: res.data.suggestion }
        }));
        toast.success('AI insight generated');
      }
    } catch (error) {
      toast.error('Failed to generate AI insight');
    } finally {
      setGeneratingStepAI(prev => ({ ...prev, [stepIndex]: false }));
    }
  };

  // Save all playbook responses
  const handleSavePlaybook = async () => {
    if (!editingPlaybook) return;

    try {
      setSavingPlaybook(true);
      let savedCount = 0;

      // Save each step that has a response
      for (const [stepIndex, data] of Object.entries(playbookResponses)) {
        if (data.response && data.response.trim()) {
          const step = editingPlaybook.steps?.[parseInt(stepIndex)];
          const noteData = {
            playbook_id: editingPlaybook.id,
            entity_type: entityType,
            entity_id: entityId,
            step_index: parseInt(stepIndex),
            question_text: step?.title || `Step ${parseInt(stepIndex) + 1}`,
            response: data.response,
            ai_suggestion: data.ai_suggestion || null,
            followup_task: null
          };

          // If note already exists, we could update it, but for now just create new
          if (!data.note_id) {
            await playbookNotesAPI.create(noteData);
            savedCount++;
          }
        }
      }

      toast.success(`Playbook saved with ${savedCount} new note${savedCount !== 1 ? 's' : ''}`);
      setShowPlaybookEditor(false);
      setEditingPlaybook(null);
      setPlaybookResponses({});
      fetchNotes();
    } catch (error) {
      toast.error('Failed to save playbook');
    } finally {
      setSavingPlaybook(false);
    }
  };

  // Group notes by playbook
  const notesByPlaybook = notes.reduce((acc, note) => {
    const key = note.playbook_id;
    if (!acc[key]) {
      acc[key] = { playbook_name: note.playbook_name, notes: [] };
    }
    acc[key].notes.push(note);
    return acc;
  }, {});

  return (
    <Card className={className} data-testid="playbook-notes-section">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <PlayCircle className="h-4 w-4 text-purple-500" />
              Playbook Notes
            </CardTitle>
            <CardDescription className="text-xs">
              Track playbook progress and AI insights
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Select onValueChange={(val) => {
              const pb = playbooks.find(p => p.id === val);
              if (pb) openPlaybookEditor(pb);
            }}>
              <SelectTrigger className="w-[160px] h-8 text-xs" data-testid="open-playbook-select">
                <FileEdit className="h-3 w-3 mr-1" />
                <SelectValue placeholder="Open Playbook" />
              </SelectTrigger>
              <SelectContent>
                {playbooks.length === 0 ? (
                  <div className="p-2 text-xs text-muted-foreground">No playbooks available</div>
                ) : (
                  playbooks.map((pb) => (
                    <SelectItem key={pb.id} value={pb.id} className="text-xs">
                      <div className="flex items-center gap-2">
                        <BookOpen className="h-3 w-3" />
                        {pb.name}
                      </div>
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            <Button size="sm" onClick={() => setShowAddDialog(true)} data-testid="add-playbook-note-btn">
              <Plus className="h-3 w-3 mr-1" />
              Add Note
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : notes.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground">
            <PlayCircle className="h-10 w-10 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No playbook notes yet</p>
            <p className="text-xs">Start tracking your sales conversations</p>
          </div>
        ) : (
          <ScrollArea className="h-[400px]">
            <div className="space-y-3">
              {Object.entries(notesByPlaybook).map(([playbookId, group]) => (
                <Collapsible key={playbookId} defaultOpen>
                  <CollapsibleTrigger className="flex items-center gap-2 w-full text-left p-2 rounded-lg hover:bg-slate-50">
                    <ChevronDown className="h-4 w-4" />
                    <span className="font-medium text-sm">{group.playbook_name}</span>
                    <Badge variant="secondary" className="ml-auto text-xs">{group.notes.length}</Badge>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pl-6 space-y-2 mt-2">
                    {group.notes.map((note) => (
                      <div 
                        key={note.id} 
                        className="p-3 border rounded-lg bg-white hover:bg-slate-50 hover:border-purple-200 transition-colors cursor-pointer"
                        data-testid={`playbook-note-${note.id}`}
                        onClick={() => openNoteDetail(note)}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            {note.question_text && (
                              <p className="text-xs text-muted-foreground mb-1">
                                Q: {note.question_text}
                              </p>
                            )}
                            <p className="text-sm line-clamp-2">{note.response}</p>
                            
                            {note.ai_suggestion && (
                              <div className="mt-2 p-2 bg-purple-50 rounded text-xs">
                                <div className="flex items-center gap-1">
                                  <Sparkles className="h-3 w-3 text-purple-500" />
                                  <span className="font-medium text-purple-700">AI Insight</span>
                                </div>
                              </div>
                            )}
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteNote(note.id);
                            }}
                            className="h-6 w-6 p-0 text-red-400 hover:text-red-600 shrink-0"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                        
                        <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                          <User className="h-3 w-3" />
                          <span>{note.created_by_name || 'Unknown'}</span>
                          <span>•</span>
                          <Clock className="h-3 w-3" />
                          <span>{formatDistanceToNow(new Date(note.created_at), { addSuffix: true })}</span>
                        </div>
                      </div>
                    ))}
                  </CollapsibleContent>
                </Collapsible>
              ))}
            </div>
          </ScrollArea>
        )}
      </CardContent>

      {/* Add Note Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PlayCircle className="h-5 w-5 text-purple-500" />
              Add Playbook Note
            </DialogTitle>
            <DialogDescription>
              Record information from your playbook conversation
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Select Playbook *</Label>
              <Select 
                value={selectedPlaybook?.id || ''} 
                onValueChange={(val) => {
                  const pb = playbooks.find(p => p.id === val);
                  setSelectedPlaybook(pb);
                  if (pb?.steps?.length > 0) {
                    setNewNote({ ...newNote, question_text: pb.steps[0]?.title || '' });
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose a playbook" />
                </SelectTrigger>
                <SelectContent>
                  {playbooks.map((pb) => (
                    <SelectItem key={pb.id} value={pb.id}>
                      {pb.name} ({pb.category})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedPlaybook && selectedPlaybook.steps && (
              <div>
                <Label>Playbook Step</Label>
                <Select 
                  value={String(newNote.step_index)} 
                  onValueChange={(val) => {
                    const idx = parseInt(val);
                    setNewNote({ 
                      ...newNote, 
                      step_index: idx,
                      question_text: selectedPlaybook.steps[idx]?.title || ''
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {selectedPlaybook.steps.map((step, idx) => (
                      <SelectItem key={idx} value={String(idx)}>
                        {idx + 1}. {step.title?.slice(0, 50) || `Step ${idx + 1}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label>Question / Topic</Label>
              <Input
                value={newNote.question_text}
                onChange={(e) => setNewNote({ ...newNote, question_text: e.target.value })}
                placeholder="What question or topic was discussed?"
              />
            </div>

            <div>
              <Label>Your Response / Notes *</Label>
              <Textarea
                value={newNote.response}
                onChange={(e) => setNewNote({ ...newNote, response: e.target.value })}
                placeholder="Record what was discussed or learned..."
                rows={3}
                data-testid="playbook-note-response"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>AI Suggestion</Label>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleGenerateAISuggestion}
                  disabled={generatingAI || !newNote.response}
                >
                  {generatingAI ? (
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  ) : (
                    <Sparkles className="h-3 w-3 mr-1" />
                  )}
                  Generate AI Insight
                </Button>
              </div>
              <Textarea
                value={newNote.ai_suggestion}
                onChange={(e) => setNewNote({ ...newNote, ai_suggestion: e.target.value })}
                placeholder="AI-generated insights will appear here..."
                rows={2}
                className="bg-purple-50"
              />
            </div>

            {/* Create Follow-up Task */}
            <Collapsible>
              <CollapsibleTrigger className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
                <ListChecks className="h-4 w-4" />
                <span>Create follow-up task</span>
                <ChevronRight className={`h-4 w-4 transition-transform ${newNote.create_task ? 'rotate-90' : ''}`} />
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-3 space-y-3 p-3 border rounded-lg">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={newNote.create_task}
                    onChange={(e) => setNewNote({ ...newNote, create_task: e.target.checked })}
                    className="rounded"
                  />
                  <Label className="text-sm">Create a task for follow-up</Label>
                </div>
                
                {newNote.create_task && (
                  <>
                    <div>
                      <Label className="text-xs">Task Title</Label>
                      <Input
                        value={newNote.task_title}
                        onChange={(e) => setNewNote({ ...newNote, task_title: e.target.value })}
                        placeholder="e.g., Follow up on pricing discussion"
                        className="h-8 text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Assign To</Label>
                      <AssigneeSelect
                        value={newNote.task_assigned_to}
                        onValueChange={(v) => setNewNote({ ...newNote, task_assigned_to: v })}
                        includeAI={true}
                        placeholder="Select assignee"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Due Date</Label>
                      <Input
                        type="date"
                        value={newNote.task_due_date}
                        onChange={(e) => setNewNote({ ...newNote, task_due_date: e.target.value })}
                        className="h-8 text-sm"
                      />
                    </div>
                  </>
                )}
              </CollapsibleContent>
            </Collapsible>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleAddNote}
              disabled={!selectedPlaybook || !newNote.response}
              data-testid="submit-playbook-note-btn"
            >
              <Send className="h-4 w-4 mr-2" />
              Save Note
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Playbook Editor Modal - Full editable playbook as template */}
      <Dialog open={showPlaybookEditor} onOpenChange={setShowPlaybookEditor}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-purple-500" />
              {editingPlaybook?.name || 'Playbook'}
              <Badge variant="outline" className="ml-2">{editingPlaybook?.category}</Badge>
            </DialogTitle>
            <DialogDescription>
              Fill out each step of the playbook. AI insights can be generated for each response.
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="flex-1 pr-4 -mr-4">
            <div className="space-y-6 py-4">
              {/* Playbook Description */}
              {editingPlaybook?.description && (
                <div className="p-3 bg-purple-50 rounded-lg border border-purple-100">
                  <p className="text-sm text-purple-800">{editingPlaybook.description}</p>
                  {editingPlaybook.target_audience && (
                    <p className="text-xs text-purple-600 mt-1">
                      <strong>Target Audience:</strong> {editingPlaybook.target_audience}
                    </p>
                  )}
                </div>
              )}

              {/* Playbook Steps */}
              {editingPlaybook?.steps?.map((step, idx) => {
                const response = playbookResponses[idx] || { response: '', ai_suggestion: '' };
                const hasExistingNote = !!response.note_id;
                
                return (
                  <div 
                    key={idx} 
                    className={`p-4 border rounded-lg ${hasExistingNote ? 'bg-green-50 border-green-200' : 'bg-white'}`}
                    data-testid={`playbook-step-${idx}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-1">
                        {hasExistingNote || response.response ? (
                          <CheckCircle2 className="h-5 w-5 text-green-600" />
                        ) : (
                          <Circle className="h-5 w-5 text-slate-300" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs font-medium text-purple-600 bg-purple-100 px-2 py-0.5 rounded">
                            Step {idx + 1}
                          </span>
                          {step.duration_minutes && (
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              ~{step.duration_minutes} min
                            </span>
                          )}
                          {hasExistingNote && (
                            <Badge variant="outline" className="text-xs bg-green-100 text-green-700 border-green-300">
                              Saved
                            </Badge>
                          )}
                        </div>
                        
                        <h4 className="font-medium text-sm mb-1">{step.title}</h4>
                        {step.description && (
                          <p className="text-xs text-muted-foreground mb-2">{step.description}</p>
                        )}
                        
                        {/* Talking Points */}
                        {step.talking_points && step.talking_points.length > 0 && (
                          <div className="mb-3">
                            <p className="text-xs font-medium text-slate-500 mb-1">Talking Points:</p>
                            <ul className="space-y-1">
                              {step.talking_points.map((point, pIdx) => (
                                <li key={pIdx} className="flex items-start gap-2 text-xs text-slate-600">
                                  <ArrowRight className="h-3 w-3 mt-0.5 text-purple-400" />
                                  {point}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {/* Response Input */}
                        <div className="space-y-2">
                          <Label className="text-xs">Your Notes / Response</Label>
                          <Textarea
                            value={response.response || ''}
                            onChange={(e) => setPlaybookResponses(prev => ({
                              ...prev,
                              [idx]: { ...prev[idx], response: e.target.value }
                            }))}
                            placeholder="Record what was discussed, customer responses, outcomes..."
                            rows={3}
                            className="text-sm"
                            disabled={hasExistingNote}
                            data-testid={`playbook-step-${idx}-response`}
                          />
                        </div>
                        
                        {/* AI Suggestion */}
                        <div className="mt-3 space-y-2">
                          <div className="flex items-center justify-between">
                            <Label className="text-xs flex items-center gap-1">
                              <Sparkles className="h-3 w-3 text-purple-500" />
                              AI Insight
                            </Label>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 text-xs"
                              onClick={() => handleGenerateStepAI(idx, step.title, response.response)}
                              disabled={generatingStepAI[idx] || !response.response || hasExistingNote}
                            >
                              {generatingStepAI[idx] ? (
                                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                              ) : (
                                <Sparkles className="h-3 w-3 mr-1" />
                              )}
                              Generate
                            </Button>
                          </div>
                          {(response.ai_suggestion || hasExistingNote) && (
                            <div className="p-2 bg-purple-50 rounded text-xs text-purple-800 border border-purple-100">
                              {response.ai_suggestion || 'No AI insight recorded'}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Objection Handlers */}
              {editingPlaybook?.objection_handlers && editingPlaybook.objection_handlers.length > 0 && (
                <div className="p-4 border rounded-lg bg-amber-50 border-amber-200">
                  <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
                    <MessageSquare className="h-4 w-4 text-amber-600" />
                    Objection Handlers
                  </h4>
                  <div className="space-y-3">
                    {editingPlaybook.objection_handlers.map((handler, idx) => (
                      <div key={idx} className="p-3 bg-white rounded border">
                        <p className="text-xs font-medium text-amber-700 mb-1">
                          Objection: &quot;{handler.objection}&quot;
                        </p>
                        <p className="text-xs text-slate-600 mb-1">
                          <strong>Response:</strong> {handler.response}
                        </p>
                        {handler.followup && (
                          <p className="text-xs text-slate-500 italic">
                            Follow-up: {handler.followup}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tips */}
              {editingPlaybook?.tips && editingPlaybook.tips.length > 0 && (
                <div className="p-4 border rounded-lg bg-blue-50 border-blue-200">
                  <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-blue-600" />
                    Tips for Success
                  </h4>
                  <ul className="space-y-1">
                    {editingPlaybook.tips.map((tip, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-xs text-blue-800">
                        <CheckCircle2 className="h-3 w-3 mt-0.5 text-blue-500" />
                        {tip}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </ScrollArea>

          <DialogFooter className="border-t pt-4 mt-2">
            <Button variant="outline" onClick={() => {
              setShowPlaybookEditor(false);
              setEditingPlaybook(null);
              setPlaybookResponses({});
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleSavePlaybook}
              disabled={savingPlaybook || Object.values(playbookResponses).every(r => !r.response?.trim())}
              data-testid="save-playbook-btn"
            >
              {savingPlaybook ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save Playbook Notes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Note Detail Dialog */}
      <Dialog open={showNoteDetail} onOpenChange={setShowNoteDetail}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-purple-500" />
              Playbook Note
            </DialogTitle>
            {selectedNote?.playbook_name && (
              <DialogDescription>
                From: {selectedNote.playbook_name}
              </DialogDescription>
            )}
          </DialogHeader>
          
          {selectedNote && (
            <div className="space-y-4">
              {/* Question */}
              {selectedNote.question_text && (
                <div className="bg-slate-50 p-3 rounded-lg">
                  <p className="text-xs font-medium text-muted-foreground mb-1">Question / Step</p>
                  <p className="text-sm font-medium">{selectedNote.question_text}</p>
                </div>
              )}
              
              {/* Response */}
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-2">Response</p>
                {editingNote ? (
                  <Textarea
                    value={editedResponse}
                    onChange={(e) => setEditedResponse(e.target.value)}
                    rows={5}
                    className="w-full"
                    placeholder="Enter your response..."
                  />
                ) : (
                  <div className="p-3 bg-white border rounded-lg">
                    <p className="text-sm whitespace-pre-wrap">{selectedNote.response}</p>
                  </div>
                )}
              </div>
              
              {/* AI Suggestion */}
              {selectedNote.ai_suggestion && (
                <div className="bg-purple-50 p-4 rounded-lg border border-purple-100">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="h-4 w-4 text-purple-500" />
                    <span className="font-medium text-purple-700">AI Insight</span>
                  </div>
                  <p className="text-sm text-purple-800 whitespace-pre-wrap">{selectedNote.ai_suggestion}</p>
                </div>
              )}
              
              {/* AI Interactions */}
              {selectedNote.ai_interactions && selectedNote.ai_interactions.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1">
                    <Bot className="h-3 w-3" />
                    AI Interactions ({selectedNote.ai_interactions.length})
                  </p>
                  <div className="space-y-2">
                    {selectedNote.ai_interactions.map((interaction) => (
                      <div key={interaction.id} className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                        <div className="flex items-center gap-2 mb-1">
                          <Bot className="h-3 w-3 text-blue-500" />
                          <span className="font-medium text-blue-700 text-sm">{interaction.agent_name}</span>
                          <span className="text-blue-400 ml-auto text-xs">
                            {formatDistanceToNow(new Date(interaction.created_at), { addSuffix: true })}
                          </span>
                        </div>
                        <p className="text-sm text-blue-800">{interaction.message}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Metadata */}
              <div className="flex items-center gap-4 text-xs text-muted-foreground border-t pt-4">
                <div className="flex items-center gap-1">
                  <User className="h-3 w-3" />
                  <span>{selectedNote.created_by_name || 'Unknown'}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  <span>{format(new Date(selectedNote.created_at), 'MMM d, yyyy h:mm a')}</span>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            {editingNote ? (
              <>
                <Button variant="outline" onClick={() => setEditingNote(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSaveNoteEdit} disabled={savingNote}>
                  {savingNote ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Save Changes
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setShowNoteDetail(false)}>
                  Close
                </Button>
                <Button variant="outline" onClick={() => setEditingNote(true)}>
                  <FileEdit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default PlaybookNotesSection;
